const { getElement } = window.BAO.Utils;

class PushCartError {
  /**
   * @param {Object} error
   *
   * @returns {void}
   */
  static async handle(error) {
    const instance = new this(error);

    if (instance.genericError) {
      return instance.render(instance.MESSAGES.GENERIC);
    } else if (instance.soldOut) {
      return instance.render(instance.MESSAGES.SOLD_OUT);
    } else if (instance.alreadyHaveAllInCart) {
      return instance.render(instance.MESSAGES.ALREADY_HAVE_ALL_STOCK);
    } else if (instance.addedMoreThanExists) {
      return instance.render(instance.MESSAGES.NOT_ENOUGH_IN_STOCK);
    } else if (instance.cannotFindVariant) {
      return instance.render(instance.MESSAGES.CANNOT_FIND_VARIANT);
    } else {
      return instance.render(instance.MESSAGES.ADD_TOO_MANY);
    }
  }

  constructor(error) {
    this.error = error;

    this.MESSAGES = {
      ADD_TOO_MANY:
        "Unfortunately, there isn't enough of this product available",
      ALREADY_HAVE_ALL_STOCK:
        "You already have all of the remaining stock in your cart",
      CANNOT_FIND_VARIANT: "There was an error adding this product to cart.",
      GENERIC: "There was an error adding this product to cart.",
      NOT_ENOUGH_IN_STOCK:
        "Unfortunately, there isn't enough of this product available",
      SOLD_OUT: "Unfortunately, this product is sold out.",
    };

    this.description = error?.description ?? "";
    this.status = error?.status ?? null;

    /**
     * @type {PushCart}
     * @public
     */
    this.pushCart = getElement("push-cart");
  }

  render(message) {
    const clone =
      this.pushCart.els["error-template"].element.content.cloneNode(true);
    const text = getElement(this.selectors.text, {
      context: clone,
    });

    text.textContent = message;

    this.pushCart.els.errors.element.appendChild(clone);

    return this;
  }

  get addedMoreThanExists() {
    return this.description.slice(0, 18) === "You can't add more";
  }

  get alreadyHaveAllInCart() {
    return this.description.slice(0, 4) === "All ";
  }

  get genericError() {
    return this.error?.errors.toLowerCase() === " error";
  }

  get soldOut() {
    return this.description.slice(0, 13) === "The product '";
  }

  get cannotFindVariant() {
    return this.description.toLowerCase() === "cannot find variant";
  }

  get selectors() {
    return {
      item: "[data-push-cart-errors-item]",
      text: "[data-push-cart-errors-text]",
      close: "[data-push-cart-errors-close]",
    };
  }
}

export class PushCart extends window.BAO.CustomElement() {
  static get requiredElements() {
    return ["error-template", "errors"];
  }

  _loading = false;

  _checkoutSpinnerActive = false;

  _checkoutClickDone = false;

  _isSubmittingCheckout = false;

  _checkoutResetTimeoutId = null;

  get loading() {
    return this._loading;
  }

  set loading(val) {
    this._loading = val;

    this.classList.toggle(this.loadingClass, val);
  }

  constructor() {
    super();

    this.sections = window.BAO.sectionRenderer.pushCartSections;

    if (this.drawer == null) {
      this.throwError("needs to be used within a <site-drawer> element");
    }
  }

  setupListeners() {
    super.setupListeners();

    this.onBeforeCartAdd = this.onBeforeCartAdd.bind(this);
    this.onAfterCartAdd = this.onAfterCartAdd.bind(this);
    this.onAfterCartUpdateRemoveItem =
      this.onAfterCartUpdateRemoveItem.bind(this);
    this.onCheckoutClick = this.onCheckoutClick.bind(this);
    this.onCheckoutSubmit = this.onCheckoutSubmit.bind(this);
    this.onPageShow = this.onPageShow.bind(this);

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    );
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_MULTIPLE_ADD,
      this.onBeforeCartAdd,
    );
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeCartAdd,
    );

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onAfterCartAdd,
    );
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_MULTIPLE_ADD,
      this.onAfterCartAdd,
    );
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAdd,
    );

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
      this.onAfterCartUpdateRemoveItem,
    );

    this.listeners.add(this, "click", this.onCheckoutClick);
    this.listeners.add(this, "submit", this.onCheckoutSubmit);
    this.listeners.add(window, "pageshow", this.onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    );

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onAfterCartAdd,
    );

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
      this.onAfterCartUpdateRemoveItem,
    );
  }

  onBeforeCartAdd() {
    this.drawers.open(this.drawer.getAttribute("key"));

    this.loading = true;
  }

  onAfterCartAdd(event) {
    const { product_id: productId } = event.detail?.state?.product_id
      ? event.detail?.state
      : event.detail?.state?.items?.[0];

    this.updatePushCartRecommendations(productId);

    if ("sections" in event.detail.state) {
      this.renderSections(event.detail.state.sections);
    }

    window.BAO.dataLayer = window.BAO.dataLayer || [];
    window.dataLayer.push({
      event: "view_cart_flyout_auto",
      anocus_shop_language: window.BAO.dataLayer.shopLanguage,
      anocus_shop_country: window.BAO.dataLayer.shopCountry,
      cart: {
        currency: window.BAO.dataLayer.cartCurrency,
        value: window.BAO.dataLayer.cartValue,
        items: window.BAO.dataLayer.items,
      },
    });

    this.loading = false;
  }

  onAfterCartUpdateRemoveItem(event) {
    const randomCartItem = event.detail?.state?.items?.sort(
      () => 0.5 - Math.random(),
    )?.[0];

    this.updatePushCartRecommendations(randomCartItem?.product_id);
  }

  /**
   * Show a spinner on the checkout button while the user is taken to the
   * checkout page
   *
   * Apps like HulkApps volume discounts bind a click handler on the document
   * which navigates straight to the checkout page, and the browser stops
   * rendering this page as soon as that navigation starts. So the click is
   * intercepted before it reaches the app, the submission is deferred until
   * the spinner has been painted, then a new click is dispatched which
   * bubbles on to the app and the form as normal
   *
   * @param {MouseEvent} event
   *
   * @returns {void}
   */
  onCheckoutClick(event) {
    const submitEl = event.target?.closest?.('[data-push-cart-el="checkout"]');

    if (!submitEl || event.button !== 0) return;

    // Swallow extra clicks while the spinner is holding
    if (this._checkoutSpinnerActive) {
      event.preventDefault();
      event.stopPropagation();

      return;
    }

    // The re-dispatched click — let it continue on to any other handlers and
    // the form's default submission
    if (event.isTrusted === false) return;

    event.preventDefault();
    event.stopPropagation();

    this._checkoutSpinnerActive = true;

    submitEl.setAttribute("aria-busy", "true");
    this.scheduleCheckoutButtonReset();

    window.setTimeout(() => {
      this._checkoutSpinnerActive = false;
      this._checkoutClickDone = true;

      // The footer section can be re-rendered while the spinner is showing, so
      // fall back to the current checkout button if the original is gone
      const target =
        submitEl.isConnected === true ? submitEl : this.els.checkout?.element;

      if (!target) {
        this._checkoutClickDone = false;
        this.resetCheckoutButton();
        return;
      }

      if (target !== submitEl) {
        target.setAttribute("aria-busy", "true");
      }

      target.dispatchEvent(
        new MouseEvent("click", { bubbles: true, cancelable: true }),
      );
    }, 400);
  }

  /**
   * Show a spinner on the checkout button while the user is taken to the
   * checkout page for submissions which were not triggered by a click on the
   * button, e.g. implicit form submission from the enter key
   *
   * The browser stops rendering this page as soon as the form starts to
   * navigate, so the submission is deferred until the spinner has been
   * painted, then the form is resubmitted programmatically
   *
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  onCheckoutSubmit(event) {
    // Only submissions of the checkout form should trigger the spinner.
    // Submit events from other forms in the drawer, e.g. the recommendation
    // add-to-cart forms, bubble up here as well
    const checkoutEl = this.els.checkout?.element;

    if (!checkoutEl || !event.target?.contains?.(checkoutEl)) return;

    // The native submission which follows the re-dispatched click — the
    // spinner is already showing, let it through
    if (this._checkoutClickDone) {
      this._checkoutClickDone = false;

      return;
    }

    const submitEl = event.submitter ?? this.els.checkout?.element;

    // The resubmission from submitCheckoutForm below — let it through natively
    if (this._isSubmittingCheckout) return;

    submitEl?.setAttribute("aria-busy", "true");
    this.scheduleCheckoutButtonReset();

    // The event is not cancelable when the submission was triggered
    // programmatically, in which case there is nothing to defer
    if (!event.cancelable) return;

    event.preventDefault();
    this._isSubmittingCheckout = true;

    window.setTimeout(() => this.submitCheckoutForm(submitEl), 400);
  }

  /**
   * @param {HTMLElement} submitEl - The checkout button which triggered the
   *   original submission
   *
   * @returns {void}
   */
  submitCheckoutForm(submitEl) {
    // The footer section can be re-rendered while the spinner is showing, so
    // fall back to the current checkout button if the original is gone
    const currentSubmitEl =
      submitEl?.isConnected === true ? submitEl : this.els.checkout?.element;
    const form = currentSubmitEl?.closest("form");

    if (!form || !currentSubmitEl) {
      this.resetCheckoutButton();
      return;
    }

    if (typeof form.requestSubmit === "function") {
      form.requestSubmit(currentSubmitEl);
    } else {
      // Older browsers do not support requestSubmit, so the checkout parameter
      // the submit button would have contributed is added to the form instead
      const hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.name = "checkout";
      hiddenInput.value = "1";
      form.appendChild(hiddenInput);

      form.submit();
    }
  }

  /**
   * If the checkout submission never happens — e.g. a third-party script
   * cancels the re-dispatched click or the deferred submission — the button
   * would stay in its busy state forever. Schedule a reset as a safety net;
   * when the page does navigate this is torn down with the page, and a
   * back/forward cache restore resets the button anyway
   *
   * @returns {void}
   */
  scheduleCheckoutButtonReset() {
    window.clearTimeout(this._checkoutResetTimeoutId);

    this._checkoutResetTimeoutId = window.setTimeout(() => {
      this.resetCheckoutButton();
    }, this.checkoutResetDelay);
  }

  resetCheckoutButton() {
    window.clearTimeout(this._checkoutResetTimeoutId);
    this._checkoutResetTimeoutId = null;

    this._checkoutSpinnerActive = false;
    this._checkoutClickDone = false;
    this._isSubmittingCheckout = false;

    this.els.checkout?.element?.setAttribute("aria-busy", "false");
  }

  /**
   * Deliberately long — it only fires when navigation has failed, and it
   * should never race a slow but successful navigation to checkout
   *
   * @returns {number}
   */
  get checkoutResetDelay() {
    return 10000;
  }

  /**
   * Reset the checkout button when the page is restored from the back/forward
   * cache, e.g. when navigating back from the checkout page
   *
   * @param {PageTransitionEvent} event
   *
   * @returns {void}
   */
  onPageShow(event) {
    if (!event.persisted) return;

    this.resetCheckoutButton();
  }

  updatePushCartRecommendations(productId) {
    if (!this.externalEls?.["push-cart-recommendations"]) return;

    this.externalEls["push-cart-recommendations"].elements?.forEach(
      (recommendationsEl) =>
        recommendationsEl.setAttribute("data-product-id", productId || ""),
    );
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  async handleError(res) {
    this.loading = false;

    return PushCartError.handle(await res.json());
  }

  get drawers() {
    return this.closest("site-drawers");
  }

  get drawer() {
    return this.closest("site-drawer");
  }

  get loadingClass() {
    return this.getAttribute("loading-class");
  }
}

if (!customElements.get("push-cart")) {
  customElements.define("push-cart", PushCart);
}
